import React, { useState, useEffect } from 'react';
import { abTestingAPI, emailTemplatesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  FlaskConical,
  Plus,
  Play,
  Pause,
  Trophy,
  TrendingUp,
  Mail,
  Bell,
  BarChart3,
  CheckCircle,
  Clock,
  RefreshCw,
  Eye,
  MousePointer,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';

const ABTesting = () => {
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [newTest, setNewTest] = useState({
    name: '',
    description: '',
    template_type: 'email',
    variant_a_id: '',
    variant_b_id: '',
    split_percentage: 50,
    target_metric: 'open_rate',
    auto_promote: false,
    min_sample_size: 100,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [testsRes, dashboardRes, templatesRes] = await Promise.all([
        abTestingAPI.list(),
        abTestingAPI.getDashboard(),
        emailTemplatesAPI.list(),
      ]);
      setTests(testsRes.data || []);
      setDashboard(dashboardRes.data);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      toast.error('Failed to load A/B testing data');
    } finally {
      setLoading(false);
    }
  };

  const createTest = async () => {
    if (!newTest.name || !newTest.variant_a_id || !newTest.variant_b_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await abTestingAPI.create(newTest);
      toast.success('A/B test created');
      setShowCreateDialog(false);
      setNewTest({
        name: '',
        description: '',
        template_type: 'email',
        variant_a_id: '',
        variant_b_id: '',
        split_percentage: 50,
        target_metric: 'open_rate',
        auto_promote: false,
        min_sample_size: 100,
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create A/B test');
    }
  };

  const updateTestStatus = async (testId, status) => {
    try {
      await abTestingAPI.update(testId, { status });
      toast.success(`Test ${status === 'paused' ? 'paused' : 'resumed'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update test');
    }
  };

  const promoteWinner = async (testId, variant) => {
    try {
      await abTestingAPI.promoteWinner(testId, variant);
      toast.success(`Variant ${variant} promoted as default`);
      fetchData();
    } catch (error) {
      toast.error('Failed to promote winner');
    }
  };

  const viewTestDetails = async (testId) => {
    try {
      const response = await abTestingAPI.get(testId);
      setSelectedTest(response.data);
    } catch (error) {
      toast.error('Failed to load test details');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ab-testing">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">A/B Testing</h1>
          <p className="text-gray-400 text-sm mt-1">Test and optimize email and notification templates</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          New A/B Test
        </Button>
      </div>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Active Tests</p>
              <p className="text-2xl font-bold text-cyan-400">{dashboard?.active_tests || 0}</p>
            </div>
            <FlaskConical className="w-8 h-8 text-cyan-400/30" />
          </CardContent>
        </Card>
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Completed</p>
              <p className="text-2xl font-bold text-green-400">{dashboard?.completed_tests || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400/30" />
          </CardContent>
        </Card>
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Email Tests</p>
              <p className="text-2xl font-bold text-purple-400">{tests.filter(t => t.template_type === 'email').length}</p>
            </div>
            <Mail className="w-8 h-8 text-purple-400/30" />
          </CardContent>
        </Card>
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Notification Tests</p>
              <p className="text-2xl font-bold text-orange-400">{tests.filter(t => t.template_type === 'notification').length}</p>
            </div>
            <Bell className="w-8 h-8 text-orange-400/30" />
          </CardContent>
        </Card>
      </div>

      {/* Tests List */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            A/B Tests
          </CardTitle>
          <CardDescription>View and manage your template experiments</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {tests.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Test Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Variants</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Target Metric</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tests.map((test) => (
                  <TableRow key={test.id} className="border-white/5">
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{test.name}</p>
                        <p className="text-gray-400 text-xs">{test.description || 'No description'}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={test.template_type === 'email' ? 'text-purple-400 border-purple-400/30' : 'text-orange-400 border-orange-400/30'}>
                        {test.template_type === 'email' ? <Mail className="w-3 h-3 mr-1 inline" /> : <Bell className="w-3 h-3 mr-1 inline" />}
                        {test.template_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">
                        <span className="text-cyan-400">A:</span> {test.variant_a_name?.slice(0, 20) || 'Variant A'}
                        <br />
                        <span className="text-orange-400">B:</span> {test.variant_b_name?.slice(0, 20) || 'Variant B'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {test.status === 'active' ? (
                        <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                      ) : test.status === 'paused' ? (
                        <Badge className="bg-yellow-500/20 text-yellow-400">Paused</Badge>
                      ) : (
                        <Badge className="bg-gray-500/20 text-gray-400">Completed</Badge>
                      )}
                    </TableCell>
                    <TableCell className="capitalize">{test.target_metric?.replace('_', ' ')}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => viewTestDetails(test.id)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {test.status === 'active' ? (
                          <Button variant="ghost" size="sm" onClick={() => updateTestStatus(test.id, 'paused')}>
                            <Pause className="w-4 h-4" />
                          </Button>
                        ) : test.status === 'paused' ? (
                          <Button variant="ghost" size="sm" onClick={() => updateTestStatus(test.id, 'active')}>
                            <Play className="w-4 h-4" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <FlaskConical className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No A/B tests yet</p>
              <p className="text-gray-500 text-sm mt-1">Create your first experiment to optimize engagement</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Test Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Create A/B Test</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-400">Test Name</Label>
              <Input
                value={newTest.name}
                onChange={(e) => setNewTest({ ...newTest, name: e.target.value })}
                placeholder="e.g., Alert Email - Subject Line Test"
                className="mt-1 bg-gray-800 border-gray-700"
              />
            </div>
            <div>
              <Label className="text-gray-400">Template Type</Label>
              <Select value={newTest.template_type} onValueChange={(v) => setNewTest({ ...newTest, template_type: v })}>
                <SelectTrigger className="mt-1 bg-gray-800 border-gray-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  <SelectItem value="email">Email Template</SelectItem>
                  <SelectItem value="notification">Notification Template</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Variant A</Label>
                <Select value={newTest.variant_a_id} onValueChange={(v) => setNewTest({ ...newTest, variant_a_id: v })}>
                  <SelectTrigger className="mt-1 bg-gray-800 border-gray-700">
                    <SelectValue placeholder="Select template" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400">Variant B</Label>
                <Select value={newTest.variant_b_id} onValueChange={(v) => setNewTest({ ...newTest, variant_b_id: v })}>
                  <SelectTrigger className="mt-1 bg-gray-800 border-gray-700">
                    <SelectValue placeholder="Select template" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-gray-400">Target Metric</Label>
              <Select value={newTest.target_metric} onValueChange={(v) => setNewTest({ ...newTest, target_metric: v })}>
                <SelectTrigger className="mt-1 bg-gray-800 border-gray-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  <SelectItem value="open_rate">Open Rate</SelectItem>
                  <SelectItem value="click_rate">Click Rate</SelectItem>
                  <SelectItem value="response_rate">Response Rate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400">Split ({newTest.split_percentage}% gets Variant A)</Label>
              <input
                type="range"
                min="10"
                max="90"
                value={newTest.split_percentage}
                onChange={(e) => setNewTest({ ...newTest, split_percentage: parseInt(e.target.value) })}
                className="w-full mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createTest}>Create Test</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Details Dialog */}
      <Dialog open={!!selectedTest} onOpenChange={() => setSelectedTest(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedTest?.name}</DialogTitle>
          </DialogHeader>
          {selectedTest?.results && (
            <div className="space-y-6 py-4">
              {/* Results comparison */}
              <div className="grid grid-cols-2 gap-4">
                {/* Variant A */}
                <Card className="bg-cyan-500/10 border-cyan-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-cyan-400 text-sm">Variant A</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs flex items-center gap-1"><Send className="w-3 h-3" /> Sent</span>
                      <span className="text-white">{selectedTest.results.variant_a?.sent || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs flex items-center gap-1"><Eye className="w-3 h-3" /> Open Rate</span>
                      <span className="text-cyan-400">{selectedTest.results.variant_a?.open_rate || 0}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs flex items-center gap-1"><MousePointer className="w-3 h-3" /> Click Rate</span>
                      <span className="text-white">{selectedTest.results.variant_a?.click_rate || 0}%</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Variant B */}
                <Card className="bg-orange-500/10 border-orange-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-orange-400 text-sm">Variant B</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs flex items-center gap-1"><Send className="w-3 h-3" /> Sent</span>
                      <span className="text-white">{selectedTest.results.variant_b?.sent || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs flex items-center gap-1"><Eye className="w-3 h-3" /> Open Rate</span>
                      <span className="text-orange-400">{selectedTest.results.variant_b?.open_rate || 0}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs flex items-center gap-1"><MousePointer className="w-3 h-3" /> Click Rate</span>
                      <span className="text-white">{selectedTest.results.variant_b?.click_rate || 0}%</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Winner and Confidence */}
              {selectedTest.results.winner && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-6 h-6 text-yellow-400" />
                    <div>
                      <p className="text-white font-medium">Winner: Variant {selectedTest.results.winner}</p>
                      <p className="text-gray-400 text-sm">Confidence: {selectedTest.results.confidence || 0}%</p>
                    </div>
                  </div>
                  {selectedTest.status !== 'completed' && (
                    <Button 
                      size="sm" 
                      onClick={() => promoteWinner(selectedTest.id, selectedTest.results.winner)}
                    >
                      Promote Winner
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ABTesting;
